import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';
import { Sparkles, Mail, Check, ShieldCheck, Loader2, PartyPopper } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EarlyAccess({ variant = 'nav' }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [over18, setOver18] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const valid = EMAIL_RE.test(email) && over18;

  const reset = () => { setEmail(''); setOver18(false); setLoading(false); setDone(false); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || loading) return;
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/early-access`, { email: email.trim(), over18: true });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const trigger = variant === 'mobile' ? (
    <button className="flex w-full items-center justify-center gap-2 px-3 py-3 rounded-md font-600 text-sm text-[#E0B85A] border border-[#E0B85A]/40 bg-[#E0B85A]/8">
      <Sparkles className="h-4 w-4" /> Join Early Access
    </button>
  ) : (
    <button className="hidden sm:flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-600 text-[#E0B85A] border border-[#E0B85A]/45 bg-[#E0B85A]/8 hover:bg-[#E0B85A]/14 hover:border-[#E0B85A]/70 transition-colors">
      <Sparkles className="h-4 w-4" /> Join Early Access
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTimeout(reset, 250); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#151119] border-white/10 text-white max-w-md w-[95vw] p-0 overflow-hidden">
        {/* Banner */}
        <div className="relative px-6 py-6 border-b border-white/8 overflow-hidden">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 120% at 20% 0%, rgba(122,24,255,0.3), transparent 55%), radial-gradient(120% 120% at 90% 20%, rgba(255,32,78,0.28), transparent 55%)' }} />
          <div className="relative">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#E0B85A] mb-1">Founders List</div>
            <h2 className="font-display font-700 text-2xl tracking-tight">Join Early Access</h2>
            <p className="text-sm text-white/60 mt-1">Be first through the gates when XINCITY™ opens. No spam — just a launch invite.</p>
          </div>
        </div>

        {done ? (
          <div className="p-8 text-center xc-fade-up">
            <div className="mx-auto h-14 w-14 rounded-full grid place-items-center mb-4" style={{ background: '#2FBF7118', border: '1px solid #2FBF7155' }}>
              <PartyPopper className="h-7 w-7 text-[#2FBF71]" />
            </div>
            <h3 className="font-display font-700 text-xl">You're on the list</h3>
            <p className="text-sm text-white/55 mt-2">We'll be in touch when XINCITY™ launches. Keep exploring the city in the meantime.</p>
            <button onClick={() => setOpen(false)} className="btn-neon rounded-xl px-6 py-3 font-600 mt-6">Back to the city</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-white/45">Email</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <input
                  type="email" inputMode="email" autoComplete="email" value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@example.com"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-[#B026FF]/50 outline-none transition-colors"
                />
              </div>
            </div>

            {/* One-click 18+ verification */}
            <button type="button" onClick={() => setOver18((v) => !v)}
              className="w-full flex items-center gap-3 rounded-lg px-4 py-3 border text-left transition-colors"
              style={{ borderColor: over18 ? '#2FBF71' : 'rgba(255,255,255,0.12)', background: over18 ? 'rgba(47,191,113,0.08)' : 'rgba(255,255,255,0.02)' }}>
              <span className="h-6 w-6 rounded-md grid place-items-center flex-shrink-0 border" style={{ borderColor: over18 ? '#2FBF71' : 'rgba(255,255,255,0.25)', background: over18 ? '#2FBF71' : 'transparent' }}>
                {over18 && <Check className="h-4 w-4 text-[#07110b]" />}
              </span>
              <span className="text-sm text-white/85 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-[#2FBF71]" /> I confirm I am 18 years or older
              </span>
            </button>

            {error && <p className="text-sm" style={{ color: '#FF5470' }}>{error}</p>}

            <button type="submit" disabled={!valid || loading}
              className="btn-neon rounded-xl w-full py-3.5 font-700 tracking-wide flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : 'Request Early Access'}
            </button>
            <p className="text-[11px] text-white/35 text-center leading-relaxed">By joining you agree to be contacted about the XINCITY™ launch. This is a prototype — no real-money gambling.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
